import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ActivityEntity, ActivityExperience, ActivityMuseum, ActivityNcc, ActivityOvernight, ActivityType, ActivityWithTime, ExperienceData, HotelData, MuseumData, NccData, TripDay } from '../../../models';
import { DatabaseEntity } from '../../../models/common.model';
import { MunicipalityService } from '../../../services/municipality.service';
import { CarRentalEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/car-rental/car-rental-edit-dialog/car-rental-edit-dialog.component';
import { ExperiencesEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/experiences/experiences-edit-dialog/experiences-edit-dialog.component';
import { ExperiencesSearchDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/experiences/experiences-search-dialog/experiences-search-dialog.component';
import { FerryTicketEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/ferry-ticket/ferry-ticket-edit-dialog/ferry-ticket-edit-dialog.component';
import { MuseumEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/museum/museum-edit-dialog/museum-edit-dialog.component';
import { MuseumSearchDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/museum/museum-search-dialog/museum-search-dialog.component';
import { NccEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/ncc/ncc-edit-dialog/ncc-edit-dialog.component';
import { NccSearchDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/ncc/ncc-search-dialog/ncc-search-dialog.component';
import { TrainTicketEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/train-ticket/train-ticket-edit-dialog/train-ticket-edit-dialog.component';
import { TripComponentEditDialogComponent } from '../../features/trip/activity-manage/activity-dialogs/trip-component/trip-component-edit-dialog/trip-component-edit-dialog.component';
import { isTimeConflict } from './time-utils';

/**
 * This contains a map of the type of the activity with the relative SEARCH dialog component.
 */
export const activitySearchDialogComponentMap: Record<string, ActivityDialogComponentData> = {
  experience: { component: ExperiencesSearchDialogComponent, dialogWidth: "1000px" },
  museum: { component: MuseumSearchDialogComponent, dialogWidth: "800px" },
  ncc: { component: NccSearchDialogComponent, dialogWidth: "1000px" }
};

/**
 * This contains a map of the type of the activity with the relative EDIT dialog component.
 */
export const activityEditDialogComponentMap: Record<string, ActivityDialogComponentData> = {
  experience: { component: ExperiencesEditDialogComponent, dialogWidth: "1200px" },
  museum: { component: MuseumEditDialogComponent, dialogWidth: "1000px" },
  ncc: { component: NccEditDialogComponent, dialogWidth: "1000px" },
  'car-rental': { component: CarRentalEditDialogComponent, dialogWidth: "1000px" },
  'train-ticket': { component: TrainTicketEditDialogComponent, dialogWidth: "1000px" },
  'ferry-ticket': { component: FerryTicketEditDialogComponent, dialogWidth: "800px" },
  'trip-component': { component: TripComponentEditDialogComponent, dialogWidth: "600px" }
};

/**
 * This interface represent an activity dialog that should be open.
 * It contains the specific component representing the activity and the dialog width.
 */
export interface ActivityDialogComponentData {
  component: ComponentType<any>,
  dialogWidth: DialogWidth
  tripAdditionalData?: any
}

export type DialogWidth = "600px" | "800px" | "1000px" | "1200px";


/**
 * This is a list of the available activity types.
 * It is used to populate the select input in the activity selection component.
 * The hotels have their own specific manage with the overnights.
 */
export const activityTypes: ActivityType[] = [
  'experience', // Experience activities (ACTIVITY WITH SEARCH)
  'ncc', // NCC activities (ACTIVITY WITH SEARCH)
  'museum', // Museum activities (ACTIVITY WITH SEARCH)
  'car-rental', // Car rental activities (ACTIVITY WITH EDIT (FLAT))
  'train-ticket', // Train ticket activities (ACTIVITY WITH EDIT (FLAT))
  'ferry-ticket', // Ferry ticket activities (ACTIVITY WITH EDIT (FLAT))
  'trip-component' // Trip component activities (ACTIVITY WITH EDIT (FLAT))
];

/**
 * This is a generic interface that maps a database entity to an activity.
 * It is used to convert a database entity to an activity object. 
 * It return an observable because it suppouse to be an asynchronous mapping.
 * 
 * Each activity type should have its own mapper that implements this interface.
 */
interface ActivityMapperAsync<T extends DatabaseEntity, E extends ActivityEntity> {
  mapFromEntityToActivity(entity: T): Observable<E>;
}


/**
* This is a generic interface that maps a database entity to an activity.
* It is used to convert a database entity to an activity object. 
* 
* Each activity type should have its own mapper that implements this interface.
*/
interface ActivityMapperSynch<T extends DatabaseEntity, E extends ActivityEntity> {
  mapFromEntityToActivity(entity: T): E;
}


@Injectable({ providedIn: 'root' })
export class OvernightHotelMapper {
  constructor(private municipalityService: MunicipalityService) { }

  /**
   * Maps a HotelData entity to an ActivityOvernight object.
   * It fetches the municipality information based on the cap and constructs the activity object.
   * 
   * @param entity - The HotelData entity to map.
   * @returns An observable of the mapped ActivityOvernight object.
   */
  mapFromEntityToActivity(entity: HotelData): Observable<ActivityOvernight> {
    return this.municipalityService.getMunicipalityByCap(entity.cap).pipe(
      map(municipality => ({
        entityId: entity.id, // Assuming entity has an id property
        name: entity.name,
        phone: entity.phone,
        description: entity.description, // TO DO da inserire quando disponiobile nel modello
        notes: entity.notes,
        advancePurchaseDays: entity.advancePurchaseDays,
        type: 'hotel',
        disclaimer: entity.disclaimer,
        rooms: [],
        totalPrice: 0,
        overnightSummary: {
          name: entity.name,
          shortAddress: municipality
            ? `${municipality.name} (${municipality.province})`
            : '',
        },
        address: entity.address,
        region: entity.region,
        nation: entity.nation,
        cap: entity.cap,
        parking: '',
        checkInFromTime: '',
        checkInToTime: '',
        checkOutTime: '',
        tdNotes: entity.notes
      }))
    );
  }
}

@Injectable({ providedIn: 'root' })
export class MuseumActivityMapper implements ActivityMapperSynch<MuseumData, ActivityMuseum> {

  /**
   * Maps a MuseumData entity to an ActivityMuseum object.
   *
   * @param entity - The MuseumData entity to map.
   * @returns The mapped ActivityMuseum object.
   */
  mapFromEntityToActivity(entity: MuseumData): ActivityMuseum {
    return {
      entityId: entity.id,
      name: entity.name,
      description: entity.description,
      disclaimer: entity.disclaimer,
      notes: entity.notes,
      type: 'museum',
      tickets: [],
      address: '',
      startTime: '',
      endTime: '',
      fullPrice: entity.priceTicketFull,
      reducedPrice: entity.priceTicketReduced,
      totalPrice: 0 // the total is initially set to 0 because the array of ticket is empty
    };
  }

}

@Injectable({ providedIn: 'root' })
export class NccActivityMapper implements ActivityMapperSynch<NccData, ActivityNcc> {

  /**
   * Maps a NccData entity to an ActivityNcc object.
   *
   * @param entity - The NccData entity to map.
   * @returns The mapped ActivityNcc object.
   */
  mapFromEntityToActivity(entity: NccData): ActivityNcc {
    return {
      entityId: entity.id,
      name: entity.ncc,
      description: entity.description,
      disclaimer: entity.disclaimer,
      phone: entity.phone,
      notes: entity.notes,
      type: 'ncc',
      startTime: '',
      endTime: null,
      startingPoint: '',
      arrivalPoint: '',
      totalPrice: 0
    };
  }

}
@Injectable({ providedIn: 'root' })
export class ExperienceActivityMapper implements ActivityMapperSynch<ExperienceData, ActivityExperience> {

  /**
   * Maps a ExperienceData entity to an ActivityExperience object.
   *
   * @param entity - The ExperienceData entity to map.
   * @returns The mapped ActivityExperience object.
   */
  mapFromEntityToActivity(entity: ExperienceData): ActivityExperience {
    return {
      entityId: entity.id,
      name: entity.title,
      duration: entity.duration,
      notes: entity.note,
      guide: entity.guide,
      disclaimer: entity.disclaimer,
      description: entity.description,
      phone: entity.phone,
      legalName: entity.legalName,
      // map the price only if is != 0 (otherwise prices will be null in the activity)
      pricePerGroup: entity.pricePerGroup == 0 ? null : entity.pricePerGroup,
      pricePerPerson: entity.pricePerPerson == 0 ? null : entity.pricePerPerson,
      totalParticipants: 0,
      meetingPoint: '',
      type: 'experience',
      startTime: '',
      endTime: '',
      totalPrice: 0
    };
  }

}

/**
 * This function checks if the provided data is an instance of DatabaseEntity.
 * It is used to ensure that the data has the required properties of a database entity.
 * It check if it contains the 'id' property that is only present in DatabaseEntity.
 * 
 * @param data - The data to check.
 * @returns True if the data is a DatabaseEntity, false otherwise.
 */
export function isEntity(data: object): data is DatabaseEntity {
  return (data as DatabaseEntity).id !== undefined;
}

/**
 * This function checks if the given activity with time overlaps with any existing activities in the current trip day.
 * @param activity - The activity with time to check for overlaps.
 * @param currentDay - The current trip day containing existing activities.
 * @param activityEditIndex - (Optional) The index of the activity being edited, to exclude it from the overlap check.
 * @returns True if there is an overlap, false otherwise.
 * 
 * It compares the start and end times of the activity with those of the existing activities to determine if there is any overlap.
 */
export function isActivityWithTimeOverlapping(activity: ActivityWithTime, currentDay: TripDay, activityEditIndex?: number): boolean {
  let activities = [...currentDay.activities];
  if (activityEditIndex != undefined) {
    // in case of edit exclude the activity being edited from the check
    activities = currentDay.activities.filter((_, index) => index !== activityEditIndex);
  }
  // 1. Validation for time conflicts
  if (activity.startTime && activity.endTime) {
    const hasConflict = activities.some(existing => {
      // car rental has the exception because its time is not relevant
      if (existing.type === 'car-rental' || activity.type === 'car-rental') {
        // skip time conflict check between two car rentals
        return false;
      }
      return isTimeConflict(activity.startTime, activity.endTime, existing.startTime, existing.endTime);
    }
    );
    return hasConflict;
  }
  return false;
}

/**
 * This map is used to translate the type of the activity into a human-readable string.
 */
export const ActivityTypeTranslationMap: Record<string, string> = {
  hotel: 'trips.activity.hotel',
  experience: 'trips.activity.experience',
  ncc: 'trips.activity.ncc',
  'additional-service': 'trips.activity.additional-service',
  museum: 'trips.activity.museum',
  'car-rental': 'trips.activity.car-rental',
  'train-ticket': 'trips.activity.train-ticket',
  'ferry-ticket': 'trips.activity.ferry-ticket',
  'trip-component': 'trips.activity.trip-component'
};